import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { indiaData } from '../../data/indianStates'; // Apne path ke hisab se check kar lena

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: any) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [step, setStep] = useState<'EMAIL' | 'OTP'>('EMAIL');
  
  // States
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Register Form Data
  const [formData, setFormData] = useState({
    name: '', phone: '', pan: '', state: '', district: '', address: ''
  });

  // Jab bhi Modal khule, purana sab data clean (blank) kar do
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setOtp('');
      setMsg({ type: '', text: '' });
      setStep('EMAIL');
      setIsRegistering(false);
      setFormData({ name: '', phone: '', pan: '', state: '', district: '', address: '' });
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'pan' ? value.toUpperCase().slice(0, 10) : value });
  };

  // --- LOGIN LOGIC ---
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });
    setIsLoading(true);
    try {
      const res = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/send_otp.php", {
      // const res = await fetch("http://localhost/seva-backend/api/auth/send_otp.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.success) {
        setStep('OTP');
        setMsg({ type: 'success', text: 'OTP sent successfully!' });
      } else {
        setMsg({ type: 'error', text: data.error || 'User not registered. Please register first.' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Server error. Try again.' });
    }
    setIsLoading(false);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // const res = await fetch("https://sevaafoundation.com/seva-backend/api/auth/verify_otp.php", {
      const res = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/verify_otp.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('sevaa_user', JSON.stringify(data.user));
        onLoginSuccess(data.user);
        onClose(); 
      } else {
        setMsg({ type: 'error', text: 'Invalid or Expired OTP' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Server error. Try again.' });
    }
    setIsLoading(false);
  };

  // --- REGISTER LOGIC ---
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // const res = await fetch("https://sevaafoundation.com/seva-backend/api/auth/register.php", {
      const res = await fetch("https://www.sevaafoundation.com/seva-backend/api/auth/register.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...formData })
      });
      const data = await res.json();
      if (data.success) {
        setMsg({ type: 'success', text: 'Registered successfully! You can now login.' });
        setFormData({ name: '', phone: '', pan: '', state: '', district: '', address: '' });
        setTimeout(() => {
          setIsRegistering(false); 
          setMsg({ type: '', text: '' }); 
        }, 1500); 
      } else {
        setMsg({ type: 'error', text: data.error || 'Registration failed' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Server error. Try again.' });
    }
    setIsLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 perspective-1000">
          <motion.div 
            style={{ transformStyle: "preserve-3d" }}
            animate={{ rotateY: isRegistering ? 180 : 0 }}
            transition={{ duration: 0.7, type: "spring", stiffness: 180, damping: 20 }}
            // Height and Width Reduced Here
            className="relative w-full max-w-[380px] h-[530px]" 
          >
            {/* FRONT SIDE - LOGIN */}
            <div 
              style={{ backfaceVisibility: "hidden" }} 
              className="absolute inset-0 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-200 font-poppins"
            >
              <div className="bg-[#0f766e] p-5 text-center relative shadow-md z-10 shrink-0">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <h2 className="text-xl font-black text-white tracking-wider uppercase">Donor Login</h2>
                <p className="text-teal-100 text-xs mt-1 font-medium">Welcome back to Sevaa Foundation</p>
              </div>
              
              <div className="p-6 flex-1 flex flex-col justify-center bg-slate-50">
                {msg.text && !isRegistering && (
                  <div className={`p-3 mb-5 rounded-lg text-xs font-bold shadow-sm border ${msg.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                    {msg.text}
                  </div>
                )}

                {step === 'EMAIL' ? (
                  <form onSubmit={handleSendOTP} className="space-y-5">
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1.5">Email Address</label>
                      <input 
                        type="email" 
                        required 
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        className="w-full p-3 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-800 shadow-sm outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all" 
                        placeholder="Enter registered email" 
                      />
                    </div>
                    <button type="submit" disabled={isLoading} className="w-full bg-[#ffb000] text-slate-900 font-black py-3.5 rounded-xl uppercase tracking-widest text-xs hover:bg-[#e6a000] shadow-md hover:shadow-lg transition-all disabled:opacity-70">
                      {isLoading ? 'Checking Details...' : 'Send OTP'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-5">
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-1.5">Enter 6-Digit OTP</label>
                      <input 
                        type="text" 
                        required 
                        value={otp} 
                        onChange={e => setOtp(e.target.value)} 
                        className="w-full p-3 bg-white border border-slate-300 rounded-xl text-center text-2xl tracking-[0.4em] font-black text-slate-800 shadow-sm outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all" 
                        placeholder="••••••" 
                        maxLength={6} 
                      />
                    </div>
                    <button type="submit" disabled={isLoading} className="w-full bg-[#0f766e] text-white font-black py-3.5 rounded-xl uppercase tracking-widest text-xs hover:bg-[#0d645c] shadow-md hover:shadow-lg transition-all disabled:opacity-70">
                      {isLoading ? 'Verifying OTP...' : 'Verify & Login'}
                    </button>
                    <button type="button" onClick={() => setStep('EMAIL')} className="w-full text-xs text-slate-500 font-bold hover:text-[#0f766e] mt-2 transition-colors">Wrong Email? Change here.</button>
                  </form>
                )}

                <div className="mt-8 text-center border-t border-slate-200 pt-6">
                  <p className="text-xs font-bold text-slate-500">Not registered with us yet?</p>
                  <button onClick={() => { setIsRegistering(true); setMsg({type:'', text:''}); }} className="mt-2 text-[#0f766e] text-sm font-black uppercase tracking-wider hover:underline transition-all">
                    Create New Account
                  </button>
                </div>
              </div>
            </div>

            {/* BACK SIDE - REGISTER */}
            <div 
              style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }} 
              className="absolute inset-0 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-200 font-poppins"
            >
              <div className="bg-[#ffb000] p-4 text-center relative shadow-md z-10 shrink-0">
                <button onClick={onClose} className="absolute top-4 left-4 text-slate-900/70 hover:text-slate-900 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <h2 className="text-lg font-black text-slate-900 tracking-wider uppercase">Quick Register</h2>
                <p className="text-slate-800 text-[10px] mt-0.5 font-bold uppercase tracking-wide">Join the Sevaa Community</p>
              </div>

              {/* Scrollbar hidden with CSS classes */}
              <div className="p-5 flex-1 overflow-y-auto bg-slate-50 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {msg.text && isRegistering && (
                   <div className={`p-2 mb-3 rounded-lg text-xs font-bold border ${msg.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                     {msg.text}
                   </div>
                )}

                <form onSubmit={handleRegister} className="space-y-3">
                  <div>
                    <input type="text" name="name" placeholder="Full Name" required value={formData.name} onChange={handleInputChange} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all" />
                  </div>
                  
                  <div>
                    <input type="email" placeholder="Email Address" required value={email} onChange={e => setEmail(e.target.value)} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <input type="tel" name="phone" placeholder="Phone Number" required value={formData.phone} onChange={handleInputChange} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all" />
                    <input type="text" name="pan" placeholder="PAN Number" required value={formData.pan} onChange={handleInputChange} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all uppercase" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <select name="state" required value={formData.state} onChange={handleInputChange} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all">
                      <option value="">Select State</option>
                      {Object.keys(indiaData).map(state => <option key={state} value={state}>{state}</option>)}
                    </select>
                    <select name="district" required value={formData.district} onChange={handleInputChange} disabled={!formData.state} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all disabled:bg-slate-100 disabled:text-slate-400">
                      <option value="">Select District</option>
                      {formData.state && indiaData[formData.state].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>

                  <div>
                    <input type="text" name="address" placeholder="Full Address" required value={formData.address} onChange={handleInputChange} className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-800 shadow-sm outline-none focus:border-[#ffb000] focus:ring-1 focus:ring-[#ffb000]/30 transition-all" />
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full mt-2 bg-slate-900 text-white font-black py-3 rounded-lg uppercase tracking-widest text-xs hover:bg-slate-800 shadow-md hover:shadow-lg transition-all disabled:opacity-70">
                    {isLoading ? 'Creating...' : 'Register'}
                  </button>
                </form>

                <div className="mt-5 text-center border-t border-slate-200 pt-4">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Already registered?</p>
                  <button 
                    type="button" 
                    onClick={() => { setIsRegistering(false); setMsg({type:'', text:''}); }} 
                    className="mt-1.5 text-[#ffb000] text-xs font-black uppercase tracking-wider hover:text-slate-900 transition-all"
                  >
                    Back to Login
                  </button>
                </div>
                
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;